/**
 * Grounding summary computation for clause review.
 *
 * Mirrors CoT partial completion semantics:
 * - "all flags located" = full success
 * - "some approximate" = partial success, verify in context
 * - "some missing" = partial failure, review carefully
 *
 * The summary is computed from per-flag grounding statuses that are
 * populated lazily as the user clicks "Locate" on individual flags.
 */
import type { FlagGroundingStatus, ClauseReviewGroundingSummary } from '@corner/shared';

/**
 * Compute a grounding summary from a map of flag ID → grounding status.
 */
export function computeGroundingSummary(
  statuses: Record<string, FlagGroundingStatus>,
  totalFlagCount: number,
): ClauseReviewGroundingSummary {
  const values = Object.values(statuses);
  const checked = values.filter((s) => s !== 'unknown');

  return {
    allExact: checked.length > 0 && checked.every((s) => s === 'exact'),
    anyApprox: checked.some((s) => s === 'approx'),
    anyMissing: checked.some((s) => s === 'missing'),
    checkedCount: checked.length,
    totalCount: totalFlagCount,
  };
}

/**
 * Derive the grounding status for a flag from a locateFlag() result.
 */
export function groundingStatusFromLocateResult(
  result: { matched: boolean; matchStartInPage: number | null },
): FlagGroundingStatus {
  if (!result.matched) return 'missing';
  // If we have a page-level match position, it's exact; otherwise approx
  return result.matchStartInPage != null ? 'exact' : 'approx';
}

/**
 * Get the user-facing grounding message for the clause review header.
 * Returns null when no flags have been checked yet (nothing to say).
 */
export function getGroundingMessage(
  summary: ClauseReviewGroundingSummary,
): { text: string; level: 'success' | 'info' | 'warning' } | null {
  if (summary.checkedCount === 0) return null;

  if (summary.anyMissing) {
    return {
      text: `${summary.checkedCount} of ${summary.totalCount} flags checked — some could not be located`,
      level: 'warning',
    };
  }

  if (summary.anyApprox) {
    return {
      text: `${summary.checkedCount} of ${summary.totalCount} flags checked — some approximately located`,
      level: 'info',
    };
  }

  if (summary.allExact) {
    const qualifier = summary.checkedCount < summary.totalCount
      ? `${summary.checkedCount} of ${summary.totalCount} flags located`
      : 'All flags located in document';
    return { text: qualifier, level: 'success' };
  }

  return null;
}
